import React, { useState, type ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { useTheme } from 'styled-components';
import { Modal, TagPill, MODAL_SIZE_MD } from '@aibuild/ui';
import type { AppCatalogEntry, AppManifest } from '@api/apps';
import { FeatureGate, useFeatureGate } from '@featureFlags/index';
import { presentationOf } from '../appPresentation';
import { localized } from '../appRunView';
import { useUserAppDraft } from '../builder/useAppBuilder';
import { returnsOf, takesOf } from './appInfo';
import { Facts, FactValue, Hero, Instructions, Steps, Term } from './AppInfoDialog.styled';

interface FactProps {
  readonly term: ReactNode;
  readonly testId?: string;
  readonly children: ReactNode;
}

const Fact: React.FC<FactProps> = ({ term, testId, children }) => (
  <>
    <Term>{term}</Term>
    <FactValue data-testid={testId}>{children}</FactValue>
  </>
);

interface AppInfoDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly manifest: AppManifest;
  readonly catalogEntry: AppCatalogEntry | null;
}

const AppInfoDialog: React.FC<AppInfoDialogProps> = ({ open, onClose, manifest, catalogEntry }) => {
  const intl = useIntl();
  const light = Boolean(useTheme().isLightTheme);
  const builderEnabled = useFeatureGate(FeatureGate.OS_APP_BUILDER);
  const owner = builderEnabled && catalogEntry?.ownedByMe === true;
  const draft = useUserAppDraft(catalogEntry?.appKey, open && owner);
  const { cover, coverLight } = presentationOf(manifest);
  const [failedHero, setFailedHero] = useState<string | null>(null);
  const themedHero = (light && coverLight) || cover;
  const hero = themedHero && themedHero !== failedHero ? themedHero : null;
  const returns = returnsOf(manifest);
  const steps = manifest.steps ?? [];
  const prompt = owner ? draft.data?.manifest.prompt : null;
  const term = (id: string, defaultMessage: string) => intl.formatMessage({ id, defaultMessage });
  const builtIn = catalogEntry !== null && (catalogEntry.source ?? 'BUILT_IN') === 'BUILT_IN';
  const madeBy = catalogEntry?.createdByName ?? (builtIn ? term('apps.run.info.byAibuild', 'Aibuild') : null);

  return (
    <Modal open={open} onClose={onClose} title={localized(intl, manifest.name)} size={MODAL_SIZE_MD} dataTestId="app-run__info-dialog">
      {hero && <Hero src={hero} alt="" onError={() => setFailedHero(hero)} data-testid="app-run__info-hero" />}
      <Facts>
        <Fact term={term('apps.run.info.description', 'What it does')}>{localized(intl, manifest.tagline)}</Fact>
        <Fact term={term('apps.run.info.takes', 'Takes')} testId="app-run__info-takes">
          {takesOf(intl, manifest)}
        </Fact>
        {returns && (
          <Fact term={term('apps.run.info.returns', 'Returns')} testId="app-run__info-returns">
            {returns}
          </Fact>
        )}
        {steps.length > 0 && (
          <Fact term={term('apps.run.info.steps', 'How it works')}>
            <Steps data-testid="app-run__info-steps">
              {steps.map((step) => (
                <li key={step.id}>{localized(intl, step.label)}</li>
              ))}
            </Steps>
          </Fact>
        )}
        {manifest.expectedDurationNote && (
          <Fact term={term('apps.run.info.duration', 'Typical duration')}>{localized(intl, manifest.expectedDurationNote)}</Fact>
        )}
        {madeBy && (
          <Fact term={term('apps.run.info.creator', 'Made by')} testId="app-run__info-creator">
            {madeBy}
          </Fact>
        )}
        {prompt && (
          <Fact
            term={
              <>
                {term('apps.run.info.instructions', 'Instructions for the agent')}
                {draft.data?.hasUnpublishedChanges && (
                  <TagPill label={term('apps.run.info.draft', 'Draft')} prefix={false} size="sm" dataTestId="app-run__info-draft" />
                )}
              </>
            }
          >
            <Instructions data-testid="app-run__info-instructions">{prompt}</Instructions>
          </Fact>
        )}
      </Facts>
    </Modal>
  );
};

export default AppInfoDialog;
